
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { ArrowRight, Smartphone, Wifi, Camera, Bluetooth, Zap, Shield, Globe, Settings, QrCode, Headphones, Watch, Router } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ProductSmartGadgets = () => {
  const navigate = useNavigate();

  const handleRequestQuote = () => {
    navigate('/', { state: { scrollToContact: true } });
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': 'Smart Gadgets & IoT Devices - 25+ Years Innovation',
    'image': [
      'https://images.unsplash.com/photo-1518788764817-60a5c814a490?w=600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&auto=format&fit=crop'
    ],
    'description': 'Innovative smart gadgets, IoT devices, and connected accessories for modern business applications. Custom development with 25+ years of hardware expertise.',
    'brand': {
      '@type': 'Brand',
      'name': 'Truecore Gadgets',
      'foundingDate': '2000'
    },
    'offers': {
      '@type': 'Offer',
      'price': '15000',
      'priceCurrency': 'INR',
      'priceValidUntil': '2025-12-31',
      'availability': 'https://schema.org/InStock'
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.7',
      'reviewCount': '156',
      'bestRating': '5',
      'worstRating': '1'
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Smart Gadgets & IoT Devices India | Custom Electronics | Truecore</title>
        <meta name="description" content="Innovative smart gadgets, IoT devices, and connected accessories manufactured in India. 25+ years expertise in custom electronics development for business applications." />
        <meta name="keywords" content="Smart gadgets India, IoT devices manufacturer, custom electronics, connected devices, smart accessories, barcode scanners, IoT sensors, smart keyboards, wireless devices, business gadgets India" />
        
        <link rel="canonical" href="https://truecoregadgets.com/products/smart-gadgets" />
        
        <meta property="og:type" content="product" />
        <meta property="og:title" content="Smart Gadgets & IoT Devices India | Custom Electronics" />
        <meta property="og:description" content="Innovative smart gadgets and IoT devices for modern business. Custom development with 25+ years hardware expertise." />
        <meta property="og:url" content="https://truecoregadgets.com/products/smart-gadgets" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1518788764817-60a5c814a490?w=600&auto=format&fit=crop" />
        
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Helmet>

      <section className="container mx-auto py-16 px-4 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 gradient-text">Smart Gadgets & IoT Innovation</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            From barcode scanners to IoT sensors - we create intelligent devices that connect your business to the digital future.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-8">
            <span className="flex items-center"><Wifi className="h-4 w-4 mr-2 text-primary" />Connected Devices</span>
            <span className="flex items-center"><Bluetooth className="h-4 w-4 mr-2 text-primary" />Wireless Tech</span>
            <span className="flex items-center"><Shield className="h-4 w-4 mr-2 text-primary" />Secure Design</span>
            <span className="flex items-center"><Globe className="h-4 w-4 mr-2 text-primary" />IoT Ready</span>
          </div>
        </div>

        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img
              src="https://images.unsplash.com/photo-1518788764817-60a5c814a490?w=600&auto=format&fit=crop"
              className="rounded-xl shadow-lg w-full object-cover"
              alt="Smart Gadgets & IoT Devices by Truecore"
              loading="lazy"
            />
          </div>
          
          <div>
            <h2 className="text-3xl font-bold mb-6">Smart Technology for Modern Business</h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <QrCode className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Data Capture Devices</h3>
                  <p className="text-muted-foreground">Advanced barcode scanners, RFID readers, and biometric devices for efficient data collection.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Wifi className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">IoT Connectivity</h3>
                  <p className="text-muted-foreground">Smart sensors and monitoring devices that connect to your business systems via WiFi, cellular, or LoRaWAN.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Camera className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Smart Accessories</h3>
                  <p className="text-muted-foreground">Professional keyboards, mice, headphones, and other peripherals designed for business use.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Custom Development</h3>
                  <p className="text-muted-foreground">Bespoke electronic solutions designed and manufactured to your exact specifications.</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Button 
                onClick={handleRequestQuote}
                className="w-full sm:w-auto flex items-center justify-center"
                size="lg"
              >
                Request Custom Quote
                <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Product Categories Tabs */}
        <Tabs defaultValue="scanners" className="mb-16">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="scanners">Scanners & Readers</TabsTrigger>
            <TabsTrigger value="iot">IoT Sensors</TabsTrigger>
            <TabsTrigger value="accessories">Accessories</TabsTrigger>
            <TabsTrigger value="custom">Custom Solutions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="scanners" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <QrCode className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Barcode Scanners</h3>
                <p className="text-muted-foreground mb-4">High-performance 1D/2D barcode scanners for retail, warehouse, and inventory applications.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• Handheld & Fixed Mount</li>
                  <li>• Wireless & Wired Options</li>
                  <li>• 1D & 2D Capability</li>
                  <li>• Industrial Durability</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹8,500</div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Smartphone className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">RFID Readers</h3>
                <p className="text-muted-foreground mb-4">Advanced RFID readers for access control, asset tracking, and inventory management.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• UHF & HF Frequency</li>
                  <li>• Long Range Reading</li>
                  <li>• Multiple Tag Detection</li>
                  <li>• SDK & API Support</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹15,000</div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Camera className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Vision Systems</h3>
                <p className="text-muted-foreground mb-4">Machine vision cameras and systems for quality control and automated inspection.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• High Resolution Imaging</li>
                  <li>• Real-time Processing</li>
                  <li>• Industrial Lighting</li>
                  <li>• AI Integration Ready</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹35,000</div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="iot" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <Globe className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Environmental Sensors</h3>
                <p className="text-muted-foreground mb-4">Monitor temperature, humidity, air quality, and other environmental parameters.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• Multi-parameter Sensing</li>
                  <li>• Wireless Connectivity</li>
                  <li>• Long Battery Life</li>
                  <li>• Cloud Integration</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹5,500</div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Zap className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Energy Monitors</h3>
                <p className="text-muted-foreground mb-4">Smart energy monitoring devices for power consumption tracking and optimization.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• Real-time Monitoring</li>
                  <li>• Load Profiling</li>
                  <li>• Energy Analytics</li>
                  <li>• Remote Control</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹12,000</div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Shield className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Security Sensors</h3>
                <p className="text-muted-foreground mb-4">Advanced motion detectors, door sensors, and security monitoring devices.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• PIR & Microwave Detection</li>
                  <li>• Tamper Protection</li>
                  <li>• Wireless Communication</li>
                  <li>• Alert Integration</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹7,800</div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="accessories" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <Settings className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Professional Keyboards</h3>
                <p className="text-muted-foreground mb-4">Mechanical and membrane keyboards designed for industrial and office environments.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• Mechanical Switches</li>
                  <li>• Backlit Options</li>
                  <li>• Programmable Keys</li>
                  <li>• Spill Resistant</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹3,500</div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Headphones className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Audio Devices</h3>
                <p className="text-muted-foreground mb-4">Professional headphones, speakers, and communication devices for business use.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• Noise Cancellation</li>
                  <li>• Wireless & Wired</li>
                  <li>• Long Battery Life</li>
                  <li>• Team Communication</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹4,200</div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Router className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Network Equipment</h3>
                <p className="text-muted-foreground mb-4">Enterprise-grade routers, switches, and access points for reliable connectivity.</p>
                <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                  <li>• WiFi 6/6E Support</li>
                  <li>• Managed Switches</li>
                  <li>• Enterprise Security</li>
                  <li>• Remote Management</li>
                </ul>
                <div className="text-lg font-semibold text-primary">From ₹8,900</div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="custom" className="mt-8">
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-6 text-center">Custom Electronic Solutions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Hardware Development</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                      <div>
                        <div className="font-medium">PCB Design & Manufacturing</div>
                        <div className="text-sm text-muted-foreground">Custom circuit boards designed to your specifications</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                      <div>
                        <div className="font-medium">Enclosure Design</div>
                        <div className="text-sm text-muted-foreground">3D modeling and manufacturing of custom housings</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                      <div>
                        <div className="font-medium">Component Integration</div>
                        <div className="text-sm text-muted-foreground">Seamless integration of sensors, displays, and interfaces</div>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-xl font-semibold mb-4">Software & Firmware</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                      <div>
                        <div className="font-medium">Embedded Programming</div>
                        <div className="text-sm text-muted-foreground">Custom firmware for microcontrollers and processors</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                      <div>
                        <div className="font-medium">Mobile Apps</div>
                        <div className="text-sm text-muted-foreground">Companion apps for device configuration and monitoring</div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                      <div>
                        <div className="font-medium">Cloud Integration</div>
                        <div className="text-sm text-muted-foreground">Backend systems for data collection and analytics</div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <Button onClick={handleRequestQuote} size="lg">
                  Start Custom Project
                  <ArrowRight className="ml-2" />
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Industries We Serve */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Industries We Serve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card p-6 rounded-lg border text-center">
              <div className="bg-primary/10 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Settings className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Manufacturing</h3>
              <p className="text-sm text-muted-foreground">Quality control, production monitoring, and automation solutions</p>
            </div>
            <div className="bg-card p-6 rounded-lg border text-center">
              <div className="bg-primary/10 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <QrCode className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Retail</h3>
              <p className="text-sm text-muted-foreground">POS systems, inventory tracking, and customer analytics</p>
            </div>
            <div className="bg-card p-6 rounded-lg border text-center">
              <div className="bg-primary/10 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Healthcare</h3>
              <p className="text-sm text-muted-foreground">Patient monitoring, asset tracking, and safety systems</p>
            </div>
            <div className="bg-card p-6 rounded-lg border text-center">
              <div className="bg-primary/10 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Globe className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Smart Cities</h3>
              <p className="text-sm text-muted-foreground">Environmental monitoring, traffic management, and infrastructure</p>
            </div>
          </div>
        </div>

        {/* Customer Success Stories */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Customer Success Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4">"Truecore developed a custom IoT monitoring system for our warehouses. The real-time temperature and humidity tracking has helped us reduce product spoilage by 30%. Excellent engineering team!"</p>
              <div className="text-sm">
                <p className="font-medium">Amit Patel</p>
                <p className="text-gray-500">Operations Director, Cold Storage Solutions</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[1, 2, 3, 4].map((star) => (
                    <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                  <svg className="w-5 h-5 text-gray-300 fill-current" viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                </div>
                <span className="ml-2 text-gray-600 text-sm">4.0</span>
              </div>
              <p className="text-gray-700 mb-4">"The barcode scanning solution has streamlined our inventory processes significantly. Integration with our existing ERP was seamless, and the devices are incredibly reliable."</p>
              <div className="text-sm">
                <p className="font-medium">Priya Sharma</p>
                <p className="text-gray-500">IT Manager, Retail Chain</p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16 bg-muted/30 px-8 py-10 rounded-xl">
          <h2 className="text-2xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-semibold text-primary mb-2">Can you develop completely custom devices?</h3>
              <p className="text-muted-foreground text-sm mb-4">Yes, we provide end-to-end custom development from concept to manufacturing, including hardware, firmware, and software.</p>
              
              <h3 className="font-semibold text-primary mb-2">What connectivity options do you support?</h3>
              <p className="text-muted-foreground text-sm mb-4">We support WiFi, Bluetooth, cellular (4G/5G), LoRaWAN, Zigbee, and other wireless protocols based on your requirements.</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-2">Do you provide software integration support?</h3>
              <p className="text-muted-foreground text-sm mb-4">Absolutely. We offer APIs, SDKs, and can integrate with your existing systems and databases.</p>
              
              <h3 className="font-semibold text-primary mb-2">What about bulk orders and enterprise pricing?</h3>
              <p className="text-muted-foreground text-sm">We offer significant discounts for bulk orders and have special enterprise pricing programs with dedicated support.</p>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Complete Technology Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Industrial Tablets</h3>
              <p className="text-muted-foreground mb-4">
                Ruggedized tablets that perfectly complement our smart devices for complete mobile solutions.
              </p>
              <Link to="/products/tablets" className="text-primary flex items-center">
                Explore Tablets
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Custom PCs</h3>
              <p className="text-muted-foreground mb-4">
                High-performance computers to process and analyze data from your smart device deployments.
              </p>
              <Link to="/products/pcs" className="text-primary flex items-center">
                Explore PCs
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-gradient-to-r from-primary/5 to-secondary/5 p-8 rounded-xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Innovate with Smart Technology?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Whether you need existing smart devices or want to develop something completely new, our team has the expertise to bring your vision to life.
          </p>
          <Button 
            onClick={handleRequestQuote}
            size="lg"
            className="text-lg px-8 py-3"
          >
            Start Your Smart Project Today
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ProductSmartGadgets;
